import { Component, Inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TalentService } from '../talent.service';
import { TestAssignmentService } from '../test-assignment.service';
import { ComposeEmailComponent } from '../compose-email/compose-email.component';

@Component({
    selector: 'app-workflow-test-assignment',
    templateUrl: './workflow-test-assignment.component.html',
    styleUrls: ['./workflow-test-assignment.component.css'],
    standalone: false
})
export class WorkflowTestAssignmentComponent implements OnInit {
  tests: any = [];
  filteredTests: any = [];
  selectedTest: any = null;
  createdAssignment: any = null;
  
  isLoading: boolean = false;
  isCreating: boolean = false;
  errorMessage: string = '';
  searchText: string = '';

  constructor(
    public dialogRef: MatDialogRef<WorkflowTestAssignmentComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private talentService: TalentService,
    private testAssignmentService: TestAssignmentService,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.loadTests();
  }

  loadTests() {
    this.isLoading = true;
    this.errorMessage = '';

    this.talentService.getCandidateTests(null).subscribe(
      (tests) => {
        this.tests = tests;
        this.filteredTests = tests;
        this.isLoading = false;
      },
      (error) => {
        console.error('Error loading tests:', error);
        this.errorMessage = 'Failed to load tests';
        this.isLoading = false;
      }
    );
  }

  applySearch() {
    if (!this.searchText) {
      this.filteredTests = this.tests;
      return;
    }

    const searchLower = this.searchText.toLowerCase();
    this.filteredTests = this.tests.filter(test => {
      return (
        (test.name && test.name.toLowerCase().includes(searchLower)) ||
        (test.skills_string && test.skills_string.toLowerCase().includes(searchLower)) ||
        (test.description && test.description.toLowerCase().includes(searchLower))
      );
    });
  }

  selectTest(test: any) {
    this.selectedTest = test;
  }

  createAssignment() {
    if (!this.selectedTest) {
      this.errorMessage = 'Please select a test';
      return;
    }

    this.isCreating = true;
    this.errorMessage = '';

    this.talentService.createTestAssignment(this.selectedTest.id, this.data.candidateId).subscribe(
      (response) => {
        this.isCreating = false;
        if (response.status === 'success') {
          this.createdAssignment = response.assignment;
        } else {
          this.errorMessage = response.message || 'Failed to create assignment';
        }
      },
      (error) => {
        this.isCreating = false;
        console.error('Error creating assignment:', error);
        this.errorMessage = 'Failed to create assignment';
      }
    );
  }

  getTestLink(): string {
    if (this.createdAssignment) {
      return this.testAssignmentService.getTestLink(this.createdAssignment.unique_code);
    }
    return '';
  }

  copyLink() {
    if (this.createdAssignment) {
      this.testAssignmentService.copyTestLink(this.createdAssignment.unique_code);
    }
  }

  sendEmail() {
    if (!this.createdAssignment) {
      return;
    }

    const testLink = this.getTestLink();
    const testName = this.selectedTest.name || `Test #${this.selectedTest.id}`;
    const candidateName = this.data.candidateName || 'there';
    
    const emailBody = `
      <p>Hello ${candidateName},</p>
      
      <p>You have been assigned a technical assessment: <strong>${testName}</strong></p>
      
      <p>Please click the link below to access your test:</p>
      <p><a href="${testLink}">${testLink}</a></p>
      
      <p>This link is unique to you. Please complete the test at your earliest convenience.</p>
      
      <p><strong>Important:</strong> Please do not use AI or cut and paste code from public sites. All such activities are monitored and logged.</p>
      
      <p>If you have any questions, please don't hesitate to reach out.</p>
      
      <p>Best regards</p>
    `;

    this.dialog.open(ComposeEmailComponent, {
      height: '600px',
      width: '800px',
      data: {
        to: this.data.candidateEmail,
        subject: `SEQ Technology - Technical Assessment - ${testName}`,
        body: emailBody,
        candidate_id: this.data.candidateId,
        is_client_contact: false
      },
    });
  }

  onClose() {
    this.dialogRef.close(this.createdAssignment ? {
      assignmentCreated: true,
      testName: this.selectedTest.name || `Test #${this.selectedTest.id}`
    } : null);
  }

  getDifficultyDisplay(difficulty: string): string {
    if (!difficulty) return 'N/A';
    return difficulty.charAt(0).toUpperCase() + difficulty.slice(1);
  }

  getDifficultyBadgeClass(difficulty: string): string {
    switch(difficulty) {
      case 'basic': return 'badge-success';
      case 'intermediate': return 'badge-warning';
      case 'advanced': return 'badge-danger';
      default: return 'badge-secondary';
    }
  }
}
